<template>
  <form :id="CHARGIFY_TARGET_NODE" ref="chargifyForm" class="flex flex-col">
    <!-- Each of these divs will be populated by Chargify's APIs. They are all separate iFrames -->
    <div :class="`px-${xMargin}`">
      <div>
        <div class="flex gap-16 w-full">
          <div id="chargify-firstname" class="flex-1" />
          <div id="chargify-lastname" class="flex-1" />
        </div>
        <div id="chargify-number" />
        <div class="chargify-fields" :class="[isSmall ? 'gap-12' : 'gap-16']">
          <div id="chargify-month" />
          <div id="chargify-year" />
          <div id="chargify-cvv" />
          <div id="chargify-zip" :class="{ 'col-span-full': isSmall }" />
        </div>
      </div>
      <slot name="cost-breakdown" />
    </div>
    <Disclaimer v-if="showDisclaimer" :class="`px-${xMargin}`" />
  </form>
</template>

<script setup lang="ts">
import { useScreenSize } from '@crossbeam/pointbreak';

import { computed, onMounted, onUnmounted, ref, watch } from 'vue';

import appConfig from '@/config';
import { CHARGIFY_TARGET_NODE } from '@/constants/billing';
import { captureException } from '@/errors';
import { useFlashesStore } from '@/stores';
import { waitForElement } from '@/utils';

import buildTailwindTheme from '../../../../../tailwind-theme-builder';

import Disclaimer from './Disclaimer.vue';

const {
  chargifyClient,
  editing = false,
  showDisclaimer = true,
} = defineProps<{
  /* eslint-disable @typescript-eslint/no-explicit-any */
  chargifyClient: any;
  editing?: boolean;
  showDisclaimer?: boolean;
}>();

const emit = defineEmits<{
  (e: 'loaded' | 'payment-portal-error'): void;
  (e: 'form-ref-saved', form: HTMLElement): void;
}>();

const chargifyForm = ref<HTMLElement | null>(null);
watch(chargifyForm, (newVal) => {
  if (newVal) {
    emit('form-ref-saved', newVal);
  }
});

const tailwindTheme = buildTailwindTheme();

const { isSmall } = useScreenSize();

const flashesStore = useFlashesStore();

const xMargin = computed(() => {
  if (isSmall.value && !editing) return 16;
  if (!isSmall.value && !editing) return 64;
  return 0;
});

/* Styles for ChargifyJS */
const style = {
  field: {},
  input: {
    fontFamily: 'Helvetica Neue',
    'border-width': '1px',
    'border-style': 'solid',
    'border-color': tailwindTheme.colors['neutral-border'],
    placeholder: { color: tailwindTheme.colors['neutral-accent'] },
    borderRadius: '8px',
    paddingTop: '4px',
    paddingBottom: '4px',
    paddingLeft: '16px',
    marginTop: '-2px',
    boxShadow: tailwindTheme.extend.boxShadow.component,
  },
  message: {
    color: tailwindTheme.colors['red-700'],
  },
  label: {
    fontSize: '12px',
    color: tailwindTheme.colors['neutral-text-strong'],
  },
};

const message = 'This field is not valid';

const CHARGIFY_CONFIG = {
  threeDSecure: true,
  selector: CHARGIFY_TARGET_NODE,
  publicKey: appConfig.chargify.publicKey,
  type: 'card',
  serverHost: appConfig.chargify.serverHost,
  addressDropdowns: true,
  hideCardImage: true /* Hides card SVG in card input */,
  requiredLabel: '',
  optionalLabel: '(Optional)',
  fields: {
    firstName: {
      selector: '#chargify-firstname',
      label: 'First Name',
      placeholder: 'First Name',
      required: true,
      message,
      style,
    },
    lastName: {
      selector: '#chargify-lastname',
      label: 'Last Name',
      placeholder: 'Last Name',
      required: true,
      message,
      style,
    },
    number: {
      selector: '#chargify-number',
      label: 'Credit card number',
      placeholder: '•••• •••• •••• ••••',
      message,
      style,
    },
    cvv: {
      selector: '#chargify-cvv',
      label: 'CVV',
      placeholder: 'CVV',
      message,
      required: true,
      style: {
        ...style,
        input: {
          ...style.input,
          paddingLeft: '14px',
          paddingRight: '14px',
        },
      },
    },
    month: {
      selector: '#chargify-month',
      label: 'Expiry Month',
      placeholder: 'MM',
      message,
      style,
    },
    year: {
      selector: '#chargify-year',
      label: 'Expiry Year',
      placeholder: 'YYYY',
      message,
      style,
    },
    zip: {
      selector: '#chargify-zip',
      label: 'Zip Code',
      placeholder: 'Zip Code',
      required: true,
      message,
      style,
    },
  },
};

async function waitForIframes() {
  let loaded = 0;
  let needsLoad = 0;
  function handleLoad() {
    loaded += 1;
    if (needsLoad === loaded) {
      emit('loaded');
    }
  }
  const selectors = Object.values(CHARGIFY_CONFIG.fields)
    .map((f) => `${f.selector} iframe`)
    .slice(0, -1);
  const promises = selectors.map((s) => waitForElement(s));
  await Promise.all(promises);
  const iFrames = selectors.map((s) => document.querySelector(s));
  needsLoad = selectors.length;
  for (const iFrame of iFrames) {
    iFrame?.addEventListener('load', handleLoad, true);
  }
}

onMounted(async () => {
  try {
    await waitForElement(`#${CHARGIFY_TARGET_NODE}`);
    chargifyClient.load(CHARGIFY_CONFIG);
    await waitForIframes();
  } catch (err) {
    captureException(err);
    flashesStore.addErrorFlash('Could not connect to payment portal');
    emit('payment-portal-error');
  }
});
onUnmounted(() => {
  try {
    chargifyClient.unload({
      selector: CHARGIFY_TARGET_NODE,
      publicKey: appConfig.chargify.publicKey,
      type: 'card',
      serverHost: appConfig.chargify.serverHost,
    });
  } catch (err) {
    captureException(err);
  }
});
</script>

<style scoped lang="pcss">
@reference "../../css/theme.css";
.chargify-fields {
  @apply grid;
  grid-template-columns: repeat(v-bind(2), 1fr);
}
</style>
